/* ------------------------------------------------------------------ */
/* leadService — unique point de sortie des demandes.                   */
/* Mode DEMO : aucune donnée ne quitte le navigateur.                   */
/* Pour brancher un backend : implémenter un LeadTransport et           */
/* l'enregistrer via setLeadTransport(). Les composants UI ne changent  */
/* pas.                                                                 */
/* ------------------------------------------------------------------ */

import type { Lead, LeadFormErrors, LeadFormValues, LeadSubmitResult, UploadFile } from "@/types";
import { needTypeById } from "@/data/needTypes";


const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]{2,}$/;
const PHONE_RE = /^[+\d][\d\s().-]{5,}$/;

export function validateLead(values: LeadFormValues, files: UploadFile[]): LeadFormErrors {
    const errors: LeadFormErrors = {};
    if (!values.company.trim()) errors.company = "Indiquez le nom de votre entreprise.";
    else if (values.company.trim().length > 160) errors.company = "160 caractères maximum.";
    if (!values.name.trim()) errors.name = "Indiquez votre nom.";
    else if (values.name.trim().length > 120) errors.name = "120 caractères maximum.";
    if (!values.email.trim()) errors.email = "Indiquez votre adresse email.";
    else if (!EMAIL_RE.test(values.email.trim()) || values.email.length > 254) errors.email = "Cette adresse email semble incorrecte.";
    if (values.phone.trim() && !PHONE_RE.test(values.phone.trim())) errors.phone = "Ce numéro de téléphone semble incorrect.";
    if (!values.needType || !needTypeById(values.needType)) errors.needType = "Sélectionnez un type de besoin.";
    if (!values.description.trim()) errors.description = "Décrivez brièvement votre besoin.";
    else if (values.description.trim().length < 20) errors.description = "Quelques mots de plus nous aideront à qualifier la demande (20 caractères minimum).";
    else if (values.description.trim().length > 10000) errors.description = "10000 caractères maximum.";
    if (values.deadline) {
        const d = new Date(values.deadline);
        if (Number.isNaN(d.getTime())) errors.deadline = "Cette date n'est pas valide.";
        else if (d < new Date(new Date().toDateString())) errors.deadline = "L'échéance indiquée est déjà passée.";
    }
    if (files.length > 8) errors.files = "8 fichiers maximum.";
    else if (files.some((f) => f.status === "unsupported")) errors.files = "Retirez les fichiers dont le format n'est pas accepté.";
    else if (files.some((f) => f.status === "too-large")) errors.files = "Retirez les fichiers trop volumineux.";
    else if (files.some((f) => f.status === "error")) errors.files = "Retirez ou remplacez les fichiers illisibles.";
    return errors;
}

export function buildLead(values: LeadFormValues, files: UploadFile[], source: string): Lead {
    return {
        company: values.company.trim(),
        name: values.name.trim(),
        email: values.email.trim().toLowerCase(),
        phone: values.phone.trim(),
        needType: (values.needType || "AUTRE") as Lead["needType"],
        description: values.description.trim(),
        deadline: values.deadline,
        desiredFormats: values.desiredFormats,
        files: files.filter((f) => f.status === "ready").map((f) => ({ name: f.name, size: f.size, extension: f.extension })),
        source,
        createdAt: new Date().toISOString(),
    };
}

export async function submitLead(lead: Lead, files: UploadFile[], key: string, website = ""): Promise<LeadSubmitResult> {
    try {
        const form = new FormData();
        form.append("payload", JSON.stringify({
            company: lead.company, name: lead.name, email: lead.email, phone: lead.phone || null,
            need_type: lead.needType, description: lead.description, deadline: lead.deadline || null,
            desired_formats: lead.desiredFormats, source: "si2t_webapp", website,
        }));
        files.filter((f) => f.status === "ready" && f.file).forEach((f) => form.append("files", f.file as File, f.name));
        const response = await fetch("/api/intake", { method: "POST", headers: { "Idempotency-Key": key }, body: form });
        const data = await response.json();
        if (!response.ok) {
            const code = typeof data?.code === "string" ? data.code : "";
            const messages: Record<string, string> = {
                VALIDATION_ERROR: "Vérifiez les champs du formulaire.", UNSUPPORTED_FILE: "Un fichier n'est pas accepté.",
                FILE_TOO_LARGE: "Un fichier dépasse 20 Mo.", TOO_MANY_FILES: "8 fichiers maximum.",
                RATE_LIMITED: "Trop de demandes récentes. Réessayez plus tard.",
                STORAGE_ERROR: "Les fichiers n'ont pas pu être enregistrés.",
                DATABASE_ERROR: "La demande n'a pas pu être enregistrée.",
            };
            throw new Error(messages[code] || "Envoi impossible pour le moment.");
        }
        if (data?.status !== "received" || typeof data.reference !== "string" || !/^SI2T-\d{4}-[A-F0-9]{16}$/.test(data.reference)) {
            throw new Error("Réponse du serveur invalide.");
        }
        return { ok: true, lead, reference: data.reference };
    } catch (error) {
        return { ok: false, message: error instanceof Error ? error.message : "Envoi impossible pour le moment." };
    }
}

export const needLabel = (id: string) => needTypeById(id)?.label ?? id;

/* ------------------------ Suggestion de type ------------------------ */

const KEYWORDS: { need: Lead["needType"]; words: string[] }[] = [
    { need: "MISE EN PLAN", words: ["mise en plan", "mettre en plan", "plan coté", "plans cotés", "cotation", "cartouche", "plan de fabrication", "plan pdf", "mise au propre"] },
    { need: "RECONCEPTION", words: ["reconception", "modifier", "modification", "reprendre", "reprise", "adapter", "existant", "ancien plan", "mettre à jour", "révision"] },
    { need: "DOSSIER FABRICATION", words: ["dossier de fabrication", "dossier fabrication", "nomenclature", "découpe", "dxf", "atelier", "fabrication", "sous-traitant"] },
    { need: "CONCEPTION 3D", words: ["conception", "concevoir", "modélisation", "modéliser", "modèle 3d", "3d", "nouvel ensemble", "nouvelle machine", "cao"] },
    { need: "RENFORT BE", words: ["renfort", "surcharge", "pic d'activité", "capacité", "dessinateur", "projeteur", "mission", "absence", "intérim"] },
    { need: "ÉTUDE TECHNIQUE", words: ["étude technique", "faisabilité", "analyse", "pré-étude", "préétude", "avant-projet", "expertise"] },
];

/** Déduction simple (mots-clés) du type de besoin à partir de la description — aucune IA, aucune requête. */
export function suggestNeedType(description: string): Lead["needType"] | null {
    const text = description.toLowerCase();
    if (text.length < 12) return null;
    let best: { need: Lead["needType"]; score: number } | null = null;
    KEYWORDS.forEach(({ need, words }) => {
        const score = words.reduce((n, w) => n + (text.includes(w) ? (w.includes(" ") ? 2 : 1) : 0), 0);
        if (score > 0 && (!best || score > best.score)) best = { need, score };
    });
    return best ? (best as { need: Lead["needType"] }).need : null;
}
